import type { Currency, Region } from '@lemonade-hq/lemonation';
import { currenciesConfig, currencyConfigPerRegion } from '@lemonade-hq/lemonation';
import { useCallback, useEffect, useMemo } from 'react';
import type { RefObject } from 'react';
import { useNumericFormat } from 'react-number-format';

export interface FormatCurrencyProps {
  readonly currency?: `${Currency}`;
  readonly region?: `${Region}`;
}

interface UseFormatCurrencyProps<TValue> {
  readonly value: TValue;
  readonly onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly disableFormat?: boolean;
  readonly formattingProps?: FormatCurrencyProps;
  readonly inputRef: RefObject<HTMLInputElement>;
}

interface UseFormatCurrencyReturnType<TValue> {
  readonly formattedValue: TValue;
  readonly onChangeWithRemoveFormatting: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function useFormatCurrency<TValue>({
  value,
  onChange,
  inputRef,
  disableFormat,
  formattingProps,
}: UseFormatCurrencyProps<TValue>): UseFormatCurrencyReturnType<TValue> {
  const numberFormatConfig = currencyConfigPerRegion[formattingProps?.region ?? 'US'];
  const symbol = currenciesConfig[formattingProps?.currency ?? numberFormatConfig.currency].symbol;

  const { format, removeFormatting, getCaretBoundary } = useNumericFormat({
    prefix: numberFormatConfig.rightSign ? undefined : symbol,
    suffix: numberFormatConfig.rightSign ? ` ${symbol}` : undefined,
    decimalScale: 2,
    allowNegative: false,
    thousandSeparator: numberFormatConfig.delimiter,
    decimalSeparator: numberFormatConfig.separator,
  });

  const formattedValue = useMemo(
    () => (value != null && format != null && !disableFormat ? format(value.toString()) : value) as TValue,
    [value, format, disableFormat],
  );

  const onChangeWithRemoveFormatting = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange != null && removeFormatting != null) {
        const unmaskedValue = disableFormat ? e.target.value : removeFormatting(e.target.value);
        onChange({ ...e, target: { ...e.target, value: unmaskedValue } });
      }
    },
    [onChange, disableFormat, removeFormatting],
  );

  useEffect(
    function moveCaretToAllowedPosition() {
      if (!disableFormat && formattedValue != null) {
        const caretBoundary = getCaretBoundary?.(formattedValue.toString());
        const currentCaretPosition = inputRef.current?.selectionStart;
        if (currentCaretPosition != null && caretBoundary != null && !caretBoundary[currentCaretPosition]) {
          const newCaretPosition = numberFormatConfig.rightSign
            ? caretBoundary.lastIndexOf(true)
            : caretBoundary.indexOf(true);

          inputRef.current?.setSelectionRange(newCaretPosition, newCaretPosition);
        }
      }
    },
    [formattedValue, disableFormat, getCaretBoundary, inputRef, numberFormatConfig],
  );

  return { formattedValue, onChangeWithRemoveFormatting };
}
